import { XMarkIcon } from "@heroicons/react/24/outline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { campaignsAPI } from "../services/campaigns";
import phoneNumbersAPI from "../services/phoneNumbers";
import { scriptsAPI } from "../services/scripts";
import LoadingSpinner from "./LoadingSpinner";

const CreateCampaignModal = ({ isOpen, onClose }) => {
    const [formData, setFormData] = useState({
        name: "",
        from_number: "",
        script: "",
        selected_script_id: "",
        selected_phone_number_id: ""
    });

    const queryClient = useQueryClient();

    // Fetch available scripts
    const { data: scriptsData, isLoading: scriptsLoading } = useQuery({
        queryKey: ['scripts'],
        queryFn: () => scriptsAPI.getScripts({ is_active: true }),
        enabled: isOpen
    });

    // Fetch available phone numbers
    const { data: phoneNumbersData, isLoading: phoneNumbersLoading } = useQuery({
        queryKey: ['available-phone-numbers'],
        queryFn: () => phoneNumbersAPI.getAvailableNumbers(),
        enabled: isOpen
    });

    const scripts = scriptsData?.scripts || [];
    const phoneNumbers = phoneNumbersData?.phoneNumbers || [];

    const createMutation = useMutation({
        mutationFn: (data) => campaignsAPI.createCampaign(data),
        onSuccess: () => {
            toast.success("Campaign created successfully!");
            queryClient.invalidateQueries({ queryKey: ["campaigns"] });
            onClose();
            resetForm();
        },
        onError: (error) => {
            toast.error(
                error?.response?.data?.message || "Failed to create campaign"
            );
        },
    });

    const resetForm = () => {
        setFormData({
            name: "",
            from_number: "",
            script: "",
            selected_script_id: "",
            selected_phone_number_id: ""
        });
    };

    // Handle script selection - populate script textarea
    const handleScriptSelect = (scriptId) => {
        const selectedScript = scripts.find(s => s.id === scriptId);
        if (selectedScript) {
            setFormData(prev => ({
                ...prev,
                selected_script_id: scriptId,
                script: selectedScript.content || ""
            }));
        }
    };

    // Handle phone number selection - populate from_number
    const handlePhoneNumberSelect = (phoneNumberId) => {
        const selectedPhone = phoneNumbers.find(p => p.id === phoneNumberId);
        if (selectedPhone) {
            setFormData(prev => ({
                ...prev,
                selected_phone_number_id: phoneNumberId,
                from_number: selectedPhone.phone_number || ""
            }));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            toast.error("Campaign name is required");
            return;
        }

        if (!formData.from_number.trim()) {
            toast.error("From number is required");
            return;
        }

        if (!formData.script.trim()) {
            toast.error("Script is required");
            return;
        }

        // Submit only the required fields (name, from_number, script)
        createMutation.mutate({
            name: formData.name,
            from_number: formData.from_number,
            script: formData.script
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                {/* Background overlay */}
                <div
                    className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
                    onClick={onClose}
                />

                {/* Modal panel */}
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">
                            Create New Campaign
                        </h3>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-500 focus:outline-none"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit}>
                        <div className="px-6 py-4 space-y-4">
                            {/* Campaign Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Campaign Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="e.g., Q4 Sales Outreach"
                                    required
                                />
                            </div>

                            {/* From Number - Dropdown */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    From Number <span className="text-red-500">*</span>
                                </label>
                                {phoneNumbersLoading ? (
                                    <LoadingSpinner size="sm" />
                                ) : (
                                    <>
                                        <select
                                            name="selected_phone_number_id"
                                            value={formData.selected_phone_number_id}
                                            onChange={(e) => handlePhoneNumberSelect(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="">Select a phone number...</option>
                                            {phoneNumbers.map((phone) => (
                                                <option key={phone.id} value={phone.id}>
                                                    {phone.phone_number} {phone.provider ? `(${phone.provider})` : ''}
                                                </option>
                                            ))}
                                        </select>
                                        <input
                                            type="text"
                                            name="from_number"
                                            value={formData.from_number}
                                            onChange={handleChange}
                                            className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Or enter phone number manually (e.g., +15551234567)"
                                        />
                                        <p className="mt-1 text-xs text-gray-500">Select from dropdown or enter manually in E.164 format</p>
                                    </>
                                )}
                            </div>

                            {/* Script - Dropdown */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Conversation Script <span className="text-red-500">*</span>
                                </label>
                                {scriptsLoading ? (
                                    <LoadingSpinner size="sm" />
                                ) : (
                                    <>
                                        <select
                                            name="selected_script_id"
                                            value={formData.selected_script_id}
                                            onChange={(e) => handleScriptSelect(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="">Select a script...</option>
                                            {scripts.map((script) => (
                                                <option key={script.id} value={script.id}>
                                                    {script.name} {script.type ? `(${script.type})` : ''}
                                                </option>
                                            ))}
                                        </select>
                                        <textarea
                                            name="script"
                                            value={formData.script}
                                            onChange={handleChange}
                                            rows={8}
                                            className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                                            placeholder={`Enter or select a script for AI to use during calls. The AI will use this as guidance to have natural, human-like conversations with contacts.\n\nExample:\nHi {first_name}, this is calling from {company}. I'm reaching out to discuss...`}
                                            required
                                        />
                                        <p className="mt-1 text-xs text-gray-500">
                                            Use variables: {"{first_name}"}, {"{last_name}"}, {"{company}"}, {"{title}"}. Select from dropdown or enter manually.
                                        </p>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                disabled={createMutation.isPending}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                disabled={createMutation.isPending}
                            >
                                {createMutation.isPending && <LoadingSpinner size="sm" className="mr-2" />}
                                {createMutation.isPending ? "Creating..." : "Create Campaign"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CreateCampaignModal;
